// ----------- Dynamic Generators -----------

import { getCanonicalUrl } from "./canonicalUrl.helper";

function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getLastSlug(route) {
  const parts = route.path.split("/").filter(Boolean);
  return parts[parts.length - 1]?.replace(/-/g, " ") || "";
}

/* ------------------- CANONICAL ------------------- */
/**
 * Generate dynamic canonical URL from route path
 * @param {Object} route - Vue Router route object
 * @returns {string} Absolute canonical URL
 * @example
 * generateCanonicalUrl({ path: '/learn/11th/11th-chemistry/47f0e821' })
 * // Returns: 'https://www.onlineselftest.com/learn/11th/11th-chemistry/47f0e821'
 */
export function generateCanonicalUrl(route) {
  return getCanonicalUrl(route.path);
}

/* ----------------- LEARN ----------------- */
export function generateLearnTitle(route) {
  const slug = getLastSlug(route);
  return `${capitalize(slug)} - Learn | OST`;
}

export function generateLearnDescription(route) {
  const slug = getLastSlug(route);
  return `Learn detailed study material for ${slug} only on OST.`;
}

export function generateLearnKeywords(route) {
  const slug = getLastSlug(route);
  return `${slug}, chapter notes, study material, OST learn`;
}

export function generateLearnOgImage(route) {
  const raw = getLastSlug(route).replace(/ /g, "-").toLowerCase();
  return `/seo/learn/${raw}.jpg`;
}

/* ----------------- QUIZ ----------------- */
export function generateQuizTitle(route) {
  const slug = getLastSlug(route);
  return `${capitalize(slug)} Quiz | OST Practice`;
}

export function generateQuizDescription(route) {
  const slug = getLastSlug(route);
  return `Attempt the ${slug} quiz and improve your performance on OST.`;
}

export function generateQuizKeywords(route) {
  const slug = getLastSlug(route);
  return `${slug}, quiz, practice test, OST quizzes`;
}

export function generateQuizOgImage(route) {
  const raw = getLastSlug(route).replace(/ /g, "-").toLowerCase();
  return `/seo/quiz/${raw}.jpg`;
}

/* --------------- COMMUNITY --------------- */
export function generateCommunityTitle(route) {
  const slug = getLastSlug(route);
  return `${capitalize(slug)} - Community Post | OST`;
}

export function generateCommunityDescription(route) {
  const slug = getLastSlug(route);
  return `Read community discussion on ${slug} at OST community.`;
}

export function generateCommunityKeywords(route) {
  const slug = getLastSlug(route);
  return `${slug}, student community, forum discussions`;
}

export function generateCommunityOgImage(route) {
  const raw = getLastSlug(route).replace(/ /g, "-").toLowerCase();
  return `/seo/community/${raw}.jpg`;
}

// ----------------- PROFILE (Dynamic) -----------------
// Generate professional profile title and description using the
// user's name, school and address. These helpers are intended
// to be called from components once the profile data is loaded.
export function generateProfileTitle(user) {
  const name = user?.user?.name || user?.name || "Student";
  const school = user?.school || user?.school_name || "";
  const city = user?.user?.city || user?.address?.city || "";
  const state = user?.user?.state || user?.address?.state || "";
  const location = [city, state].filter(Boolean).join(", ");

  if (school && location) {
    return `${name} — ${school} (${location}) | OST Profile`;
  }
  if (school) {
    return `${name} — ${school} | OST Profile`;
  }
  if (location) {
    return `${name} — ${location} | OST Profile`;
  }
  return `${name} | OST Profile`;
}

export function generateProfileDescription(user) {
  const name = user?.user?.name || user?.name || "This student";
  const school = user?.school || user?.school_name || null;
  const city = user?.user?.city || user?.address?.city || null;
  const state = user?.user?.state || user?.address?.state || null;
  const location = [city, state].filter(Boolean).join(", ");

  const schoolPart = school ? `a student at ${school}` : "a student";
  const locationPart = location ? ` in ${location}` : "";

  return `${name} is ${schoolPart}${locationPart}. View their achievements, study groups, resources and recent activity on OnlineSelfTest.`;
}

export function generateProfileKeywords(user) {
  const name = user?.user?.name || user?.name || "student";
  const school = user?.school || user?.school_name || "";
  const city = user?.user?.city || user?.address?.city || "";
  return `${name}${school ? `, ${school}` : ""}${city ? `, ${city}` : ""}, student profile, achievements, study groups`;
}

export function generateProfileOgImage(user) {
  // Prefer profile picture if available, otherwise fallback to a default image
  const picture = user?.user?.profile_picture || user?.profile_picture;
  if (picture) return picture.startsWith("http") ? picture : `/uploads/${picture}`;
  return "/seo/profile-default.jpg";
}

// -----------------------------------------------------------
// ---------------------- SEO CONFIG -------------------------
// -----------------------------------------------------------

export const SEO_CONFIG = {
  "/": {
  "title": "Online Self Test, Quizzes & Exam Practice Platform for Students | OnlineSelfTest",
  "description": "OnlineSelfTest helps students learn, practice, and compete with AI-powered self tests, quizzes, mock exams, complete solutions, and learning resources. Earn scores and use them to pay your monthly subscription.",
  "keywords": "online self test, student practice tests, online quizzes for students, exam preparation platform, free learning tests, score based learning, competitive student platform",
  "image": "/seo/home-og.jpg",
  "ogType": "website",
  "twitterCard": "summary_large_image",
  "canonical": "https://www.onlineselftest.com/"
},

"/home": {
  "title": "Practice Tests, Quizzes & Smart Learning for Students | OnlineSelfTest",
  "description": "Practice chapter-wise quizzes, mock tests, and self assessments with complete solutions on OnlineSelfTest. Track performance, earn scores, and learn smarter with free and premium options.",
  "keywords": "online practice tests, student self assessment, chapter wise quizzes, smart learning platform, free exam practice, online student tests",
  "image": "/seo/home-og.jpg",
  "ogType": "website",
  "twitterCard": "summary_large_image",
  "canonical": "https://www.onlineselftest.com/"
},

"/gauth": {
  "title": "Google Sign-In Callback | OnlineSelfTest",
  "description": "Complete Google sign-in to access your OnlineSelfTest account and continue learning.",
  "keywords": "google sign-in, oauth callback, authentication, online self test",
  "image": "/seo/auth/google-og.jpg",
  "ogType": "website",
  "twitterCard": "summary_large_image",
  "canonical": "https://www.onlineselftest.com/gauth"
},

"/fauth": {
  "title": "Facebook Sign-In Callback | OnlineSelfTest",
  "description": "Complete Facebook sign-in to access your OnlineSelfTest account and continue learning.",
  "keywords": "facebook sign-in, oauth callback, authentication, online self test",
  "image": "/seo/auth/facebook-og.jpg",
  "ogType": "website",
  "twitterCard": "summary_large_image",
  "canonical": "https://www.onlineselftest.com/fauth"
},

"/learn": {
  "title": "Learn with Complete Study Resources & Solutions | OST",
  "description": "Access structured learning resources, subject-wise notes, explanations, and solutions on OnlineSelfTest to learn better, score higher, and compete confidently.",
  "keywords": "online learning resources, CBSE study materials, subject wise notes, exam preparation resources, student learning platform"
},

"/quiz": {
  "title": "Online Quizzes & Self Assessment Tests | OST",
  "description": "Attempt topic-wise and chapter-wise online quizzes to test your knowledge, improve accuracy, and boost exam readiness with instant results.",
  "keywords": "online quizzes for students, self assessment tests, chapter wise quiz, practice quiz platform"
},

"/community": {
  "title": "Student Learning Community & Discussions | OST",
  "description": "Join the OnlineSelfTest student community to discuss questions, share knowledge, collaborate with peers, and grow together academically.",
  "keywords": "student learning community, online student discussions, academic forum, peer learning platform"
},

"/about-us": {
  "title": "About OnlineSelfTest – Learn, Compete & Score Better",
  "description": "OnlineSelfTest is a smart self-learning and testing platform helping students learn, practice, compete with peers, and earn scores for subscriptions.",
  "keywords": "about online self test, student assessment platform, learning and testing solution, education technology platform"
},

"/contact": {
  "title": "Contact OnlineSelfTest Support Team | OST",
  "description": "Get in touch with OnlineSelfTest for support, queries, feedback, or assistance related to tests, learning resources, or subscriptions.",
  "keywords": "contact online self test, student support, education platform contact, OST help"
},

"/faq": {
  "title": "Frequently Asked Questions | OnlineSelfTest",
  "description": "Find answers to common questions about OnlineSelfTest features, quizzes, scoring system, subscriptions, and learning resources.",
  "keywords": "online self test faq, student quiz help, learning platform questions, OST support"
},

"/privacy-policy": {
  "title": "Privacy Policy | OnlineSelfTest",
  "description": "Learn how OnlineSelfTest protects your personal data, ensures privacy, and maintains secure access for students and educators.",
  "keywords": "online self test privacy policy, student data protection, education platform privacy"
},

"/terms": {
  "title": "Terms & Conditions | OnlineSelfTest",
  "description": "Read the terms and conditions governing the use of OnlineSelfTest services, features, subscriptions, and student access.",
  "keywords": "online self test terms, student platform terms, usage conditions"
},

"/cookies-policy": {
  "title": "Cookies Policy | OnlineSelfTest",
  "description": "Understand how OnlineSelfTest uses cookies to improve user experience, performance, and personalized learning features.",
  "keywords": "cookies policy education website, online self test cookies, student platform cookies"
},

"/profile-setting": {
  "title": "Profile Settings & Preferences | OST",
  "description": "Manage your profile details, learning preferences, and account settings to personalize your OnlineSelfTest experience.",
  "keywords": "student profile settings, learning preferences, online test account settings"
},

"/dashboard": {
  "title": "Student Dashboard – Progress, Scores & Insights | OST",
  "description": "View your learning progress, quiz scores, achievements, and personalized insights from your OnlineSelfTest dashboard.",
  "keywords": "student dashboard, learning progress tracker, quiz performance analytics"
},

"/assignments": {
  "title": "Assignments & Practice Tasks | OnlineSelfTest",
  "description": "Complete assignments and practice tasks designed to strengthen concepts, improve accuracy, and enhance exam performance.",
  "keywords": "student assignments online, practice tasks, concept strengthening exercises"
},

"/achievements": {
  "title": "Achievements & Performance Milestones | OST",
  "description": "Track your academic achievements, earned scores, and learning milestones as you progress on OnlineSelfTest.",
  "keywords": "student achievements, learning milestones, quiz rewards system"
},

"/analytics": {
  "title": "Learning Analytics & Performance Reports | OST",
  "description": "Analyze your strengths, weaknesses, accuracy, and improvement areas with detailed learning analytics and reports.",
  "keywords": "learning analytics, student performance reports, quiz analysis"
},

"/goals": {
  "title": "Set Learning Goals & Track Progress | OST",
  "description": "Define your study goals, monitor progress, and stay focused on achieving higher scores and better academic outcomes.",
  "keywords": "study goals tracker, student goal setting, academic progress"
},

"/tests": {
  "title": "Online Tests & Exams Practice | OnlineSelfTest",
  "description": "Attempt structured online tests and exams to evaluate readiness, improve confidence, and perform better in real exams.",
  "keywords": "online tests for students, exam practice platform, self evaluation exams"
},

"/practice-tests": {
  "title": "Timed Practice Tests for Exam Preparation | OST",
  "description": "Practice with timed tests that simulate real exam conditions to improve speed, accuracy, and confidence.",
  "keywords": "timed practice tests, exam simulation tests, online mock exams"
},

"/quick-quiz": {
  "title": "Quick Quizzes for Instant Practice | OST",
  "description": "Take short and focused quizzes for fast revision, instant practice, and quick knowledge assessment.",
  "keywords": "quick quizzes, instant practice tests, fast revision quizzes"
},

"/flashcards": {
  "title": "Interactive Flashcards for Smart Learning | OST",
  "description": "Revise key concepts quickly using interactive flashcards designed for effective memory retention.",
  "keywords": "educational flashcards, concept revision tools, smart learning flashcards"
},

"/study-groups": {
  "title": "Online Study Groups for Collaborative Learning | OST",
  "description": "Learn together by joining study groups, sharing knowledge, solving doubts, and preparing collaboratively.",
  "keywords": "online study groups, collaborative learning, peer study platform"
},

"/forum": {
  "title": "Student Q&A Forum – Ask, Answer & Learn | OST",
  "description": "Ask questions, share answers, and learn from fellow students through the OnlineSelfTest Q&A forum.",
  "keywords": "student question answer forum, academic discussion board, learning forum"
},

"/leaderboard": {
  "title": "Student Leaderboard & Rankings | OnlineSelfTest",
  "description": "Check rankings, compare scores, and see how you perform against other students and schools.",
  "keywords": "student leaderboard, academic rankings, competitive learning platform"
},

"/profile": {
  "title": "Student Profile & Performance Overview | OST",
  "description": "View your learning journey, scores, achievements, and activity history in one place.",
  "keywords": "student profile page, learning history, performance overview"
},

"/study-goals": {
  "title": "Personal Study Goals & Progress Tracking | OST",
  "description": "Set clear academic goals, track progress, and stay motivated throughout your learning journey.",
  "keywords": "personal study goals, academic planning, progress tracking"
},

"/assignmentpage": {
  "title": "Student Assignments & Submissions | OST",
  "description": "View, complete, and manage assignments to strengthen concepts and improve academic performance.",
  "keywords": "online student assignments, assignment submission platform"
},

"/schools-groups-features": {
  "title": "School & Group Learning Platform with Tests, Analytics & Admin Tools | OnlineSelfTest",
  "description": "OnlineSelfTest for Schools and Groups offers centralized admin control, student assessments, detailed analytics, reports, integrations, and scalable plans for institutions to manage and improve academic performance.",
  "keywords": "school assessment platform, online testing for schools, group learning system, student performance analytics, education management platform, institutional learning tools"
},

"/individual-learning-features": {
  "title": "Individual Learning Plan, Practice Tests & Analytics for Students | OnlineSelfTest",
  "description": "Explore OnlineSelfTest’s Individual Learning Plan with smart practice tests, quizzes, flashcards, analytics, goals, achievements, and competitive leaderboards. Learn better, score higher, and earn rewards to access premium learning.",
  "keywords": "individual learning plan, online self test features, student practice tests, learning analytics for students, quiz platform with solutions, competitive learning platform"
},

"/communities": {
  "title": "Learning Communities for Students | OST",
  "description": "Explore multiple student communities focused on learning, practice, competition, and academic growth.",
  "keywords": "student communities, online learning groups, academic collaboration"
},


  // ⭐ DYNAMIC ROUTES (wildcards)


 "/learn/": {
    title: (route) => generateLearnTitle(route),
    description: (route) => generateLearnDescription(route),
    keywords: (route) => generateLearnKeywords(route),
    image: (route) => generateLearnOgImage(route),
    canonical: (route) => generateCanonicalUrl(route),
    ogType: "article",
    twitterCard: "summary_large_image"
  },

  "/quiz/": {
    title: (route) => generateQuizTitle(route),
    description: (route) => generateQuizDescription(route),
    keywords: (route) => generateQuizKeywords(route),
    image: (route) => generateQuizOgImage(route),
    canonical: (route) => generateCanonicalUrl(route),
    ogType: "article",
    twitterCard: "summary_large_image"
  },

  "/community/": {
    title: (route) => generateCommunityTitle(route),
    description: (route) => generateCommunityDescription(route),
    keywords: (route) => generateCommunityKeywords(route),
    image: (route) => generateCommunityOgImage(route),
    canonical: (route) => generateCanonicalUrl(route),
    ogType: "article",
    twitterCard: "summary_large_image"
  },


  "/communities/": {
    title: (route) => generateCommunityTitle(route),
    description: (route) => generateCommunityDescription(route),
    keywords: (route) => generateCommunityKeywords(route),
    image: (route) => generateCommunityOgImage(route),
    canonical: (route) => generateCanonicalUrl(route),
    ogType: "article",
    twitterCard: "summary_large_image"
  },

  "/dashboard/": {
    title: (route) => generateCommunityTitle(route),
    description: (route) => generateCommunityDescription(route),
    keywords: (route) => generateCommunityKeywords(route),
    image: (route) => generateCommunityOgImage(route),
    canonical: (route) => generateCanonicalUrl(route),
    ogType: "article",
    twitterCard: "summary_large_image"
  },

  
  "/profile/": {
    title: (route) => generateCommunityTitle(route),
    description: (route) => generateCommunityDescription(route),
    keywords: (route) => generateCommunityKeywords(route),
    image: (route) => generateCommunityOgImage(route),
    canonical: (route) => route.fullPath,
    ogType: "article",
    twitterCard: "summary_large_image"
  }

};
